import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileText, CheckCircle, Clock, TrendingUp, Zap } from 'lucide-react';
import HowItWorks from './HowItWorks';
import StudentTestimonials from './StudentTestimonials';
import { CampaignBoard } from '../components/dashboard/CampaignBoard';
import RecentDocuments from '../components/dashboard/RecentDocuments';
import SubscriptionCard from '../components/dashboard/SubscriptionCard';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../lib/constants';

// Clean, pulsing skeleton layout matching the metrics blocks
const MetricSkeleton = () => (
  <div className="h-6 w-16 bg-white/10 rounded animate-pulse" />
);

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, subscription, isPremium } = useAuth();
  const { stats, loading } = useDashboardStats();

  useEffect(() => {
    if (window.location.hash === '#how-it-works-section') {
      const element = document.getElementById('how-it-works-section');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('payment') === 'success') {
      toast.success('Welcome to Premium! Your account has been upgraded.', {
        duration: 6000,
        icon: '🎓',
      });
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [location, navigate]);
  
  const firstName = profile?.full_name?.split(' ')[0] || 'Student';

  return (
    <div className="w-full animate-fade-in">
      {/* Welcome Section */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-brown-900 mb-2 leading-tight">
          Welcome back, {firstName} 👋
        </h1>
        <div className="text-xs sm:text-base md:text-lg text-brown-600 h-6 flex items-center">
          {loading ? (
            <div className="h-4 w-48 bg-brown-200 rounded animate-pulse" />
          ) : isPremium ? (
            "You're all set with Premium. Enjoy unlimited uploads." 
          ) : (
            `You have ${stats?.remaining_uploads || 0} uploads remaining for today.`
          )}
        </div>
      </div>
      
      <CampaignBoard />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Left Dynamic Premium Hero Card */}
        <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-gradient-to-br from-brown-900 via-brown-950 to-stone-900 p-6 text-white shadow-xl border border-brown-800/40 flex flex-col justify-between min-h-[200px]">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
          <div>
            <span className="text-[11px] font-bold tracking-wider text-amber-400 uppercase opacity-90">
              OmniClarix Document Performance
            </span>
            <div className="flex items-baseline space-x-2 mt-2">
              <span className="text-5xl font-black tracking-tight text-cream-50 min-w-[3rem]">
                {loading ? <div className="h-12 w-16 bg-white/10 rounded animate-pulse mt-1" /> : (stats?.total_uploads || 0)}
              </span>
              {!loading && <span className="text-sm font-medium text-stone-400">Total Completed Analysis</span>}
            </div>
          </div>

          {/* Progress Tracking Bar Block */}
          <div className="mt-6 pt-4 border-t border-white/5">
            <div className="flex justify-between items-center text-xs font-semibold mb-2">
              <span className="text-stone-400">Daily Usage Limit</span>
              <span className="text-amber-300 font-mono">
                {loading ? (
                  <div className="h-3 w-24 bg-white/10 rounded animate-pulse" />
                ) : isPremium ? (
                  "∞ (Premium Active)" 
                ) : (
                  `${stats?.today_uploads || 0} / ${(stats?.today_uploads || 0) + (stats?.remaining_uploads || 0)} Uploads`
                )}
              </span>
            </div>
            <div className="w-full bg-stone-800 rounded-full h-2 overflow-hidden p-[1px] border border-stone-700/30">
              <div 
                className="bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-300 h-full rounded-full transition-all duration-500 ease-out shadow-[0_0_12px_rgba(245,158,11,0.3)]"
                style={{ width: loading ? '0%' : isPremium ? '100%' : `${((stats?.today_uploads || 0) / ((stats?.today_uploads || 0) + (stats?.remaining_uploads || 0) || 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Right Supporting Sidebar Status Counters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
          <div className="bg-gradient-to-r from-brown-900 to-brown-950 p-4 rounded-xl border border-brown-800/30 flex items-center justify-between shadow-md">
            <div>
              <p className="text-xs font-semibold text-stone-400 tracking-wide">In Progress</p>
              <div className="text-2xl font-bold text-cream-50 mt-0.5">
                {loading ? <MetricSkeleton /> : (stats?.processing_documents || 0)}
              </div>
            </div>
            <div className="p-2.5 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <Clock className={`w-5 h-5 text-amber-400 ${loading ? 'animate-pulse' : 'animate-spin-slow'}`} />
            </div>
          </div>

          <div className="bg-gradient-to-r from-brown-900 to-brown-950 p-4 rounded-xl border border-brown-800/30 flex items-center justify-between shadow-md">
            <div>
              <p className="text-xs font-semibold text-stone-400 tracking-wide">Successfully Processed</p>
              <div className="text-2xl font-bold text-emerald-400 mt-0.5">
                {loading ? <MetricSkeleton /> : (stats?.completed_documents || 0)}
              </div>
            </div>
            <div className="p-2.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-brown-900 to-brown-950 p-4 rounded-xl border border-brown-800/30 flex items-center justify-between shadow-md">
            <div>
              <p className="text-xs font-semibold text-stone-400 tracking-wide">Account Status</p>
              <div className="text-sm font-bold text-cream-100 mt-1">
                {loading ? <div className="h-4 w-28 bg-white/10 rounded animate-pulse" /> : isPremium ? "Premium Engine Access" : "Basic Free Tier Account"}
              </div>
            </div>
            <div className={`p-2.5 rounded-lg border ${isPremium ? 'bg-amber-500/10 border-amber-500/20' : 'bg-stone-800 border-stone-700'}`}>
              <FileText className={`w-5 h-5 ${isPremium ? 'text-amber-400' : 'text-stone-400'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
        <div className="lg:col-span-2 space-y-4 sm:space-y-5 md:space-y-6">
          <RecentDocuments limit={5} />

          <div className="hidden lg:block backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-brown-900 mb-4">Quick Insights</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-green-50/50 to-green-100/30 border border-green-200/50">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <p className="text-xs font-medium text-green-800">Success Rate</p>
                </div>
                <div className="text-2xl font-bold text-green-900">
                  {loading ? (
                    <div className="h-7 w-12 bg-green-200/50 rounded animate-pulse" />
                  ) : (
                    `${stats?.total_uploads > 0 ? Math.round((stats.completed_documents / stats.total_uploads) * 100) : 0}%`
                  )}
                </div>
              </div>
              <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-blue-50/50 to-blue-100/30 border border-blue-200/50">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <p className="text-xs font-medium text-blue-800">Avg. Process Time</p>
                </div>
                <p className="text-2xl font-bold text-blue-900">~2m</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-4 sm:space-y-5 md:space-y-6">
          <SubscriptionCard profile={profile} subscription={subscription} />

          <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-brown-900 mb-3 sm:mb-4">Quick Actions</h3>
            <div className="space-y-2 sm:space-y-3">
              <button onClick={() => navigate(ROUTES.UPLOAD)} className="w-full flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-brown-500/10 to-brown-600/5 hover:from-brown-500/20 hover:to-brown-600/10 rounded-lg sm:rounded-xl transition-all group border border-brown-200/50">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 sm:p-2 bg-brown-600 rounded-lg">
                    <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <span className="text-sm sm:text-base font-medium text-brown-900">Upload New Document</span>
                </div>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-brown-400 group-hover:text-brown-700 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 sm:mt-16 border-t border-cream-300/50 pt-12">
        <HowItWorks />
      </div>
      <div className="mt-12 sm:mt-16 border-t border-cream-300/30 pt-12">
        <StudentTestimonials />
      </div>
    </div>
  );
};

export default Dashboard;